import as from 'ansis'
import { Command } from 'commander'

import { accessTokenPayloadSchema } from '@scalar-org/entities/token'
import { generateTokenCallbackEndpoint } from '@scalar-org/mongobase-client/auth/node'
import { decode } from '@scalar-org/tokens'

import { config } from '@/config'
import { loggedInMessage } from '@/domains/auth/login/helpers/messages'
import { generateAccessByPersonalToken } from '@/domains/auth/login/helpers/personal-token-generate-access'
import { setAuthData } from '@/helpers'
import { output } from '@/helpers/std-out'
import { openBrowser } from '@/helpers/url-opener'

import { emailPasswordAuth } from './helpers/email-pass-auth'

export const LoginCommand = () => {
  const cmd = new Command('login')

  cmd.description('Login to scalar')
  cmd.option('--email <email>', 'Email')
  cmd.option('--password <password>', 'Password')
  cmd.option('--token <token>', 'Personal token')
  cmd.action(async ({ email, password, token: _token }) => {
    // Attempt to fetch a token from the environment variables
    const token = process.env.SCALAR_API_KEY || _token
    // If personal token is provided we try to generate a new access token from the personal token
    if (token) {
      // Try to generate a new access token
      try {
        const { accessToken } = await generateAccessByPersonalToken({
          personalToken: token,
        })
        const { email: userEmail } = decode(
          accessToken,
          accessTokenPayloadSchema,
        )

        output.info().message(loggedInMessage(userEmail)).print()
      } catch (err: any) {
        return output
          .error()
          .title('Invalid token')
          .message(err.message ?? 'Please provide a valid personal token')
          .exit('error')
      }

      return
    }

    // Allow user to provide username/password combination to login for ci flow
    if (email || password) {
      if (!email || !password) {
        return output
          .error()
          .title('Please provide both email and password combination')
          .message()
          .exit('error')
      }

      try {
        const { accessToken, refreshToken } = await emailPasswordAuth({
          email,
          password,
        })
        await setAuthData({ accessToken, refreshToken })

        output.info().message(loggedInMessage(email)).print()
      } catch (err: any) {
        output.error().title(err.message).print()
      }
      return
    }

    const { port, getTokens } = await generateTokenCallbackEndpoint(
      `${config.services.api}/core/login/exchange`,
    )

    const loginUrl = `${config.projects.dashboard}/login?externalRedirect=local&port=${port}`

    output
      .info()
      .message(`Please follow the link to login: ${as.cyan(loginUrl)}`)
      .print()

    // Try to open the login url
    openBrowser(loginUrl)

    try {
      // Wait for the user to finish with the login flow
      const tokenResult = await getTokens()
      if (tokenResult.error) throw Error('Unable to exchange token')

      // Saving tokens locally
      await setAuthData(tokenResult.data)

      const { email: userEmail } = decode(
        tokenResult.data.accessToken,
        accessTokenPayloadSchema,
      )
      return output.info().message(loggedInMessage(userEmail)).exit('success')
    } catch {
      return output
        .error()
        .title('Something went wrong. Please try again.')
        .exit('error')
    }
  })

  return cmd
}
